import { getCadDisplayMode, isCadMapDisplay, isLikelyWgs84 } from './cadGeometry';
import { canLocateCadParcelOnCountryMap } from './cadRegistros';
import { readCadProjectParcelCount } from './cadParcelDisplay';
import { cadListPinImageId, resolveCadListPinFillColor } from './cadListPinIcon';

const EMPTY_COLLECTION = { type: 'FeatureCollection', features: [] };

/**
 * Polígono WGS84 del GET `?include=geometry`. Nunca metros DXF.
 * @param {unknown} geometry
 * @returns {boolean}
 */
export function isCadOwnerMapGeometry(geometry) {
  if (!geometry || typeof geometry !== 'object') return false;
  const type = geometry.type;
  if (type !== 'Polygon' && type !== 'MultiPolygon') return false;
  return isLikelyWgs84(geometry);
}

/**
 * Pintar polígono en el mapa país.
 * @param {object|null|undefined} row
 * @returns {boolean}
 */
export function canPaintCadOwnerPolygon(row) {
  return row?.display_mode === 'map' && isCadOwnerMapGeometry(row.geometry);
}

/**
 * Pin a zoom bajo: modo map y centroide WGS84.
 * @param {object|null|undefined} row
 * @returns {boolean}
 */
export function canPaintCadOwnerPin(row) {
  return canLocateCadParcelOnCountryMap(row);
}

/**
 * FeatureCollection para el overlay del dueño. El front lo arma (BE manda filas).
 * @param {object[]} parcels
 * @returns {{ type: 'FeatureCollection', features: object[] }}
 */
export function buildCadOwnerMapFeatureCollection(parcels) {
  const features = (Array.isArray(parcels) ? parcels : [])
    .filter(canPaintCadOwnerPolygon)
    .map((row) => {
      const id = String(row.id);
      return {
        type: 'Feature',
        id,
        geometry: row.geometry,
        properties: {
          id,
          title: row.title || null,
          name: row.name || null,
          stock_status: row.stock_status || 'available',
          price: row.price ?? null,
          currency: row.currency || null,
          area_m2: row.area_m2 ?? null,
          project_slug: row.project_slug,
          project_name: row.project_name || null,
          project_id: row.project_id || null,
          display_mode: row.display_mode,
          can_manage: row.can_manage === true,
          can_publish: row.can_publish === true,
        },
      };
    });
  return { type: 'FeatureCollection', features };
}

/**
 * Filas para pines (`useListPinsLayer` lee `id` + `lat`/`lng`).
 * @param {object[]} parcels
 * @returns {object[]}
 */
export function buildCadOwnerMapPinLots(parcels) {
  return (Array.isArray(parcels) ? parcels : []).filter(canPaintCadOwnerPin);
}

/**
 * Centroide WGS84 de `GET /getcadprojects`. Canvas / fuera de Argentina: no.
 *
 * @param {object|null|undefined} project
 * @returns {boolean}
 */
export function canPaintCadListProjectPin(project) {
  return Boolean(readCadListPreviewCenter(project));
}

/** Zoom de preview al abrir una lista desde el pin / card, antes del GET. */
export const CAD_LIST_PREVIEW_ZOOM = 16;

/**
 * Centro del pin de lista (`lat`/`lng` de getcadprojects).
 *
 * @param {object|null|undefined} list
 * @returns {{ lng: number, lat: number }|null}
 */
export function readCadListPreviewCenter(list) {
  if (!isCadMapDisplay(list)) return null;
  const lat = Number(list?.lat);
  const lng = Number(list?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (!isLikelyWgs84({ type: 'Point', coordinates: [lng, lat] })) return null;
  return { lng, lat };
}

/**
 * Salta al centroide de la lista sin animar. El overlay después encuadra los lotes.
 *
 * @param {import('maplibre-gl').Map|null|undefined} map
 * @param {object|null|undefined} list
 * @param {{ padding?: object|number, zoom?: number }} [options]
 * @returns {boolean}
 */
export function jumpMapToCadListPreview(map, list, { padding, zoom = CAD_LIST_PREVIEW_ZOOM } = {}) {
  const center = readCadListPreviewCenter(list);
  if (!map || !center || typeof map.fitBounds !== 'function') return false;
  const delta = 0.004;
  try {
    map.fitBounds(
      [
        [center.lng - delta, center.lat - delta],
        [center.lng + delta, center.lat + delta],
      ],
      {
        padding,
        duration: 0,
        maxZoom: zoom,
      },
    );
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Actualiza la fila del índice de pines con lo que acaba de guardar
 * Referenciar. Sin mapa → sin centroide: el pin no puede quedar fantasma.
 *
 * @param {object} row
 * @param {object} project
 * @returns {object}
 */
export function syncCadListPinRow(row, project) {
  if (!row || !project) return row;
  const onMap = isCadMapDisplay(project);
  const nextLat = Number(project.lat);
  const nextLng = Number(project.lng);
  const lotCount = readCadProjectParcelCount(project);
  return {
    ...row,
    display_mode: getCadDisplayMode(project),
    lat: onMap ? (Number.isFinite(nextLat) ? nextLat : row.lat) : null,
    lng: onMap ? (Number.isFinite(nextLng) ? nextLng : row.lng) : null,
    ...(lotCount != null ? { parcel_count: lotCount } : {}),
  };
}

function isSameCadListIndexRow(row, project) {
  const projectId = String(project?.id || '').trim();
  const projectSlug = String(project?.slug || '').trim();
  if (projectId && String(row?.id || '') === projectId) return true;
  if (projectSlug && String(row?.slug || '') === projectSlug) return true;
  return false;
}

/**
 * Reaplica el último Referenciar sobre un GET de pines. Si el listado
 * todavía dice `map` y el proyecto ya no, el pin no reviva.
 *
 * @param {object[]} projects
 * @param {object|null|undefined} pending
 * @returns {{ projects: object[], pending: object|null }}
 */
export function applyCadListPinSync(projects, pending) {
  const list = Array.isArray(projects) ? projects : [];
  if (!pending) return { projects: list, pending: null };
  let stillPending = false;
  const next = list.map((row) => {
    if (!isSameCadListIndexRow(row, pending)) return row;
    if (isCadMapDisplay(row) !== isCadMapDisplay(pending)) stillPending = true;
    return syncCadListPinRow(row, pending);
  });
  return { projects: next, pending: stillPending ? pending : null };
}

/**
 * Un pin por lista. `parcel_count` = tooltip (mismo número que la card).
 *
 * @param {object[]} projects
 * @returns {object[]}
 */
export function buildCadListMapPins(projects) {
  return (Array.isArray(projects) ? projects : [])
    .filter(canPaintCadListProjectPin)
    .map((project) => {
      const fillColor = resolveCadListPinFillColor(project.brand);
      return {
        id: project.id || null,
        slug: project.slug || '',
        name: project.name || project.slug || 'Lista',
        display_mode: 'map',
        can_manage: project.can_manage === true,
        can_publish: project.can_publish === true,
        lotCount: readCadProjectParcelCount(project) ?? 0,
        lat: Number(project.lat),
        lng: Number(project.lng),
        fillColor,
        icon: cadListPinImageId(fillColor),
      };
    });
}

/**
 * Con la lista abierta ese pin no se pinta: el click no debe reabrirla
 * ni tapar el loteo. Tras Quitar del mapa, el índice puede llegar tarde.
 *
 * @param {object[]} pins
 * @param {object|null|undefined} project
 * @returns {object[]}
 */
export function omitCadListPinsForProject(pins, project) {
  const list = Array.isArray(pins) ? pins : [];
  if (!project || project === true) return list;
  const projectId = String(project.id || project.project_id || '').trim();
  const projectSlug = String(project.slug || '').trim();
  if (!projectId && !projectSlug) return list;
  return list.filter((pin) => {
    if (projectId && String(pin.id || '') === projectId) return false;
    if (projectSlug && String(pin.slug || '') === projectSlug) return false;
    return true;
  });
}

/**
 * Hover del pin de lista. Título = nombre; cuerpo = N lotes.
 *
 * @param {{ name?: string, lotCount?: number }} [pin]
 * @returns {string|null}
 */
export function buildCadListPinTooltipHtml(pin = {}) {
  const name = String(pin.name || '').trim();
  const count = Number(pin.lotCount);
  const lotsLabel = Number.isFinite(count)
    ? (count === 1 ? '1 lote' : `${count} lotes`)
    : '';
  if (!name && !lotsLabel) return null;
  const escape = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  const parts = [];
  if (name) parts.push(`<div class="terrio-place-tooltip__name">${escape(name)}</div>`);
  if (lotsLabel) parts.push(`<div class="terrio-place-tooltip__title">${escape(lotsLabel)}</div>`);
  return parts.join('');
}

/**
 * @param {object[]} parcels
 * @returns {Map<string, object>}
 */
export function indexCadOwnerParcelsById(parcels) {
  const next = new Map();
  (Array.isArray(parcels) ? parcels : []).forEach((row) => {
    if (!row?.id) return;
    next.set(String(row.id), row);
  });
  return next;
}

export function emptyCadOwnerFeatureCollection() {
  return EMPTY_COLLECTION;
}

/**
 * Saca del overlay las filas de un proyecto (lista) que ya no existe.
 * Matchea por `project_id` o `project_slug`.
 *
 * @param {object[]} parcels
 * @param {{ id?: string, slug?: string }} [project]
 * @returns {object[]}
 */
/**
 * ¿La fila o las properties de un feature son de este proyecto?
 * Acepta `projectId` / `id` / `slug` (el alcance del panel usa `projectId`).
 * @param {object|null|undefined} row
 * @param {{ projectId?: string, id?: string, slug?: string }} [project]
 */
export function matchesCadOwnerProject(row, project = {}) {
  const projectId = project.projectId ?? project.id;
  const slug = project.slug;
  if (projectId != null && String(projectId) !== ''
    && String(row?.project_id || '') === String(projectId)) {
    return true;
  }
  if (slug != null && String(slug).trim()
    && String(row?.project_slug || '') === String(slug).trim()) {
    return true;
  }
  return false;
}

/**
 * Features del overlay del dueño que pertenecen a la lista CAD del alcance.
 * @param {object[]} features
 * @param {{ projectId?: string, id?: string, slug?: string }} [project]
 */
export function filterCadOwnerFeaturesForProject(features, project) {
  return (Array.isArray(features) ? features : [])
    .filter((feature) => matchesCadOwnerProject(feature?.properties || feature, project));
}

export function dropCadOwnerParcelsForProject(parcels, project = {}) {
  const rows = Array.isArray(parcels) ? parcels : [];
  const projectId = project.id != null && project.id !== '' ? String(project.id) : '';
  const projectSlug = project.slug != null && String(project.slug).trim()
    ? String(project.slug).trim()
    : '';
  if (!projectId && !projectSlug) return rows;
  return rows.filter((row) => {
    if (projectId && String(row?.project_id || '') === projectId) return false;
    if (projectSlug && String(row?.project_slug || '') === projectSlug) return false;
    return true;
  });
}
